import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Risk Assessment subsection adapter
 * Handles complex risk assessments including ScoredChecklist components (18 fields)
 * This is one of the more complex subsections with various risk factors and safety measures
 */
export class RiskAssessmentAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hospitalization risk assessment
    const hospitalizationRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK
    )?.at(0)

    const hospitalizationRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
    )

    const hospitalizationRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
    )?.at(0)

    // Emergency planning
    const triageCode = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE
    )?.at(0)

    const emergencyPlan = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN
    )?.at(0)

    const safeRoom = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM
    )

    // Fire safety assessment
    const fireAssessmentOxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN
    )?.at(0)

    const fireSafetyPrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
    )

    // Homebound status
    const homeboundStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS
    )?.at(0)

    // Infection risk assessment
    const infectionRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK
    )?.at(0)

    const infectionRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS
    )

    const infectionRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
    )?.at(0)

    // Fall risk assessment (ScoredChecklist component)
    const fallRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK
    )?.at(0)

    const fallRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS
    )

    const fallRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
    )?.at(0)

    const fallRiskAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT
    )

    // Financial and environmental safety
    const financeRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK
    )?.at(0)

    const financeRiskOther = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER
    )?.at(0)

    const environmentSafety = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY
    )

    const nurseSafetyRisks = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS
    )

    const safetyMeasures = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES
    )

    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES
    )?.at(0)

    return {
      [DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]: hospitalizationRisk,
      [DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]: hospitalizationRiskFactors,
      [DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]:
        hospitalizationRiskFactorsOther,
      [DischargeFormField.RISK_ASSESSMENT_TRIAGE_CODE]: triageCode,
      [DischargeFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]: emergencyPlan,
      [DischargeFormField.RISK_ASSESSMENT_SAFE_ROOM]: safeRoom,
      [DischargeFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]: fireAssessmentOxygen,
      [DischargeFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]: fireSafetyPrecautions,
      [DischargeFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]: homeboundStatus,
      [DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK]: infectionRisk,
      [DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]: infectionRiskFactors,
      [DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]: infectionRiskFactorsOther,
      [DischargeFormField.RISK_ASSESSMENT_FALL_RISK]: fallRisk,
      [DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]: fallRiskFactors,
      [DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]: fallRiskFactorsOther,
      [DischargeFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]: fallRiskAssessment,
      [DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK]: financeRisk,
      [DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]: financeRiskOther,
      [DischargeFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]: environmentSafety,
      [DischargeFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]: nurseSafetyRisks,
      [DischargeFormField.RISK_ASSESSMENT_SAFETY_MEASURES]: safetyMeasures,
      [DischargeFormField.RISK_ASSESSMENT_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS,
        value: this.getFormDataField(
          DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
        )
      })
    }
    if (
      this.formDataFieldExists(
        DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
      )
    ) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          DischargeFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_TRIAGE_CODE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_TRIAGE_CODE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_SAFE_ROOM)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_SAFE_ROOM)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          DischargeFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FALL_RISK)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FALL_RISK)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_SAFETY_MEASURES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_SAFETY_MEASURES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.RISK_ASSESSMENT_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES,
        value: this.getFormDataField(DischargeFormField.RISK_ASSESSMENT_NOTES)
      })
    }

    return mutations
  }
}
